import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
} from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { PlusIcon } from "lucide-react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import type { FormsListQueryDTO } from "@/modules/forms/domain/contracts/forms";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import {
  useDeleteFormMutation,
  useFormCategoriesLookupQuery,
  useFormsListQuery,
} from "../api/queries";
import { FormsTable } from "../ui/forms-list/FormsTable";

export function FormsListPage() {
  const nav = useNavigate({ from: "/app/forms" });
  const search = useSearch({ from: "/app/forms" });

  const query: FormsListQueryDTO = {
    limit: search.limit ?? 20,
    offset: search.offset ?? 0,
    ...(search.status ? { status: search.status } : {}),
    ...(search.categoryId ? { categoryId: search.categoryId } : {}),
  };

  const list = useFormsListQuery(query);
  const categoriesLookup = useFormCategoriesLookupQuery();
  const deleteMutation = useDeleteFormMutation();
  const canCreate = useActionPermission("forms:create");
  const canUpdate = useActionPermission("forms:update");
  const canDelete = useActionPermission("forms:delete");

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຟອມ", to: "/app/forms/dashboard" },
            { label: "ຟອມ" },
          ]}
        />
      </Header>
      <Main>
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h1 className="font-semibold text-2xl tracking-tight">ຟອມ</h1>
              <p className="text-muted-foreground text-sm">
                ຈັດການຟອມ ລິ້ງ public ແລະ ຜົນຕອບ
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex flex-col gap-1.5">
                <span className="text-muted-foreground text-xs">ສະຖານະ</span>
                <Select
                  value={search.status ?? "all"}
                  onValueChange={(v) => {
                    const next: typeof search = { ...search, offset: 0 };
                    if (v === "all") {
                      const { status: _s, ...rest } = next;
                      void _s;
                      nav({ search: rest });
                    } else {
                      nav({
                        search: {
                          ...next,
                          status: v as FormsListQueryDTO["status"],
                        },
                      });
                    }
                  }}
                >
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="ທັງໝົດ" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">ທັງໝົດ</SelectItem>
                    <SelectItem value="draft">ຮ່າງ</SelectItem>
                    <SelectItem value="published">ເປີດຮັບ</SelectItem>
                    <SelectItem value="closed">ປິດ</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-1.5">
                <span className="text-muted-foreground text-xs">ຫມວດຫມູ່</span>
                <Select
                  value={search.categoryId ?? "all"}
                  onValueChange={(v) => {
                    const next: typeof search = { ...search, offset: 0 };
                    if (v === "all") {
                      const { categoryId: _c, ...rest } = next;
                      void _c;
                      nav({ search: rest });
                    } else {
                      nav({ search: { ...next, categoryId: v } });
                    }
                  }}
                >
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="ທັງໝົດ" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">ທັງໝົດ</SelectItem>
                    {(categoriesLookup.data ?? []).map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {canUpdate ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => nav({ to: "/app/forms/categories" })}
                >
                  ຫມວດຫມູ່
                </Button>
              ) : null}
              {canCreate ? (
                <Button
                  type="button"
                  onClick={() => nav({ to: "/app/forms/new" })}
                  className="shrink-0"
                >
                  <PlusIcon className="mr-2 h-4 w-4" />
                  ຟອມໃໝ່
                </Button>
              ) : null}
            </div>
          </div>

          <div className="rounded-xl border bg-card">
            <FormsTable
              data={list.data?.data ?? []}
              isLoading={list.isLoading}
              offset={query.offset}
              limit={query.limit}
              totalCount={list.data?.meta.total ?? 0}
              canUpdate={canUpdate}
              canDelete={canDelete}
              onPaginationChange={(offset, limit) =>
                nav({ search: { ...search, offset, limit } })
              }
              onEdit={(id) =>
                nav({ to: "/app/forms/$id/edit", params: { id } })
              }
              onResponses={(id) =>
                nav({ to: "/app/forms/$id/responses", params: { id } })
              }
              onDelete={
                canDelete
                  ? (id) =>
                      toast.promise(deleteMutation.mutateAsync(id), {
                        loading: "ກຳລັງລຶບຟອມ...",
                        success: "ລຶບແລ້ວ",
                        error: "ລຶບບໍ່ສຳເລັດ",
                      })
                  : undefined
              }
            />
          </div>
        </div>
      </Main>
    </>
  );
}
